import { MockExam3Question } from '../data/mockExam3Questions';

interface Exam3QuestionCardProps {
  question: MockExam3Question;
  questionNumber: number;
  totalQuestions: number;
  selectedAnswer: string | null;
  onAnswerSelect: (answer: string) => void;
  showSolution: boolean;
}

const DOMAIN_BADGE: Record<string, string> = {
  'Algebra': 'bg-indigo-100 text-indigo-700',
  'Advanced Math': 'bg-fuchsia-100 text-fuchsia-700',
  'Problem-Solving & Data Analysis': 'bg-lime-100 text-lime-700',
  'Geometry & Trigonometry': 'bg-red-100 text-red-700',
};

export default function Exam3QuestionCard({
  question,
  questionNumber,
  totalQuestions,
  selectedAnswer,
  onAnswerSelect,
  showSolution,
}: Exam3QuestionCardProps) {
  const isAnswered = selectedAnswer !== null;

  const getOptionStyle = (label: string) => {
    if (!isAnswered) {
      return selectedAnswer === label
        ? 'border-indigo-500 bg-indigo-50'
        : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer';
    }
    if (label === question.correctAnswer) return 'border-emerald-500 bg-emerald-50';
    if (label === selectedAnswer && label !== question.correctAnswer) return 'border-red-400 bg-red-50';
    return 'border-gray-200 bg-gray-50 opacity-60';
  };

  const getOptionIcon = (label: string) => {
    if (!isAnswered) return null;
    if (label === question.correctAnswer)
      return <i className="ri-check-circle-fill text-emerald-500 text-xl w-6 h-6 flex items-center justify-center" />;
    if (label === selectedAnswer && label !== question.correctAnswer)
      return <i className="ri-close-circle-fill text-red-500 text-xl w-6 h-6 flex items-center justify-center" />;
    return null;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-gray-500">
            Question {questionNumber} of {totalQuestions}
          </span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DOMAIN_BADGE[question.domain]}`}>
            {question.domain}
          </span>
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {question.subtopic}
          </span>
        </div>
        {isAnswered && (
          <span className={`text-sm font-bold ${selectedAnswer === question.correctAnswer ? 'text-emerald-600' : 'text-red-500'}`}>
            {selectedAnswer === question.correctAnswer ? '✓ Correct' : '✗ Incorrect'}
          </span>
        )}
      </div>

      <p
        className="text-lg text-gray-800 leading-relaxed mb-7 font-medium"
        dangerouslySetInnerHTML={{ __html: question.question }}
      />

      <div className="space-y-3 mb-6">
        {question.options.map((opt) => (
          <button
            key={opt.label}
            onClick={() => !isAnswered && onAnswerSelect(opt.label)}
            disabled={isAnswered}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-between ${getOptionStyle(opt.label)}`}
          >
            <div className="flex items-center gap-4">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                isAnswered && opt.label === question.correctAnswer
                  ? 'bg-emerald-500 text-white'
                  : isAnswered && opt.label === selectedAnswer && opt.label !== question.correctAnswer
                  ? 'bg-red-400 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {opt.label}
              </span>
              <span className="text-gray-800" dangerouslySetInnerHTML={{ __html: opt.value }} />
            </div>
            {getOptionIcon(opt.label)}
          </button>
        ))}
      </div>

      {showSolution && isAnswered && (
        <div className="bg-gradient-to-br from-indigo-50 to-fuchsia-50 rounded-xl p-6 border border-indigo-200 mt-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
              <i className="ri-lightbulb-line text-white text-sm" />
            </div>
            <h3 className="font-bold text-gray-900">Step-by-Step Solution</h3>
          </div>
          <ol className="space-y-2 mb-4">
            {question.solution.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                <span className="w-5 h-5 bg-indigo-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span dangerouslySetInnerHTML={{ __html: step }} />
              </li>
            ))}
          </ol>
          <p className="text-sm text-gray-600 italic border-t border-indigo-200 pt-3">
            {question.solution.explanation}
          </p>
        </div>
      )}
    </div>
  );
}
